import { useAuthStore } from '../stores/auth'
import type { SignInResponse } from '../types/api'

// Relativ '/api' → wird im Dev-Betrieb vom Vite-Proxy zum Backend getunnelt
// (same-origin, CORS-frei). Für Direktbetrieb: VITE_API_BASE_URL setzen.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api'

// Pfade, bei denen ein 401 NICHT einen Refresh-Retry auslöst
const NO_RETRY_PATHS = ['/auth/signin', '/auth/signup', '/auth/refresh']

export class ApiError extends Error {
    status: number

    constructor(message: string, status: number) {
        super(message)
        this.status = status
    }
}

let refreshPromise: Promise<boolean> | null = null

/**
 * Erneuert Access+Refresh-Token über den httpOnly-Cookie.
 * Single-Flight: parallele 401er teilen sich denselben Refresh.
 */
function refreshTokens(): Promise<boolean> {
    if (!refreshPromise) {
        refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        })
            .then(async res => {
                if (!res.ok) return false
                const data = (await res.json()) as SignInResponse
                useAuthStore.getState().setSession(data.accessToken, data.user)
                return true
            })
            .catch(() => false)
            .finally(() => {
                refreshPromise = null
            })
    }
    return refreshPromise
}

interface RequestOptions {
    method?: string
    body?: unknown
    signal?: AbortSignal
}

export async function apiRequest<T>(
    path: string,
    options: RequestOptions = {},
    allowRetry = true,
): Promise<T> {
    const { accessToken } = useAuthStore.getState()
    const res = await fetch(`${API_BASE}${path}`, {
        method: options.method ?? 'GET',
        headers: {
            ...(options.body !== undefined
                ? { 'Content-Type': 'application/json' }
                : {}),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
        credentials: 'include',
        signal: options.signal,
    })

    if (res.status === 401 && allowRetry && !NO_RETRY_PATHS.includes(path)) {
        const refreshed = await refreshTokens()
        if (refreshed) return apiRequest<T>(path, options, false)
        useAuthStore.getState().clear()
        throw new ApiError('Sitzung abgelaufen — bitte neu anmelden.', 401)
    }

    if (res.status === 204) return undefined as T

    const text = await res.text()
    const body = text ? (JSON.parse(text) as unknown) : null

    if (!res.ok) {
        const message =
            (body as { message?: string | string[] } | null)?.message ??
            `Anfrage fehlgeschlagen (${res.status})`
        throw new ApiError(
            Array.isArray(message) ? message.join(' · ') : message,
            res.status,
        )
    }
    return body as T
}

/**
 * Lädt eine Datei (PDF/CSV) mit Auth-Header und löst den Browser-Download aus.
 */
export async function downloadFile(
    path: string,
    filename: string,
): Promise<void> {
    const { accessToken } = useAuthStore.getState()
    const res = await fetch(`${API_BASE}${path}`, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        credentials: 'include',
    })
    if (!res.ok) throw new ApiError('Download fehlgeschlagen.', res.status)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
}

export { refreshTokens }
